/**
 * This class is used to create a worker using `importScripts` for loading a script from a different origin.
 * @see https://github.com/webpack/webpack/discussions/14648#discussioncomment-1589272
 *
 * When using with Webpack, alias CORSWorker to Worker to make the import statically analyzable.
 *
 * e.g.
 *  import Worker from 'CORSWorker';
 *
 *  const corsWorker = new Worker(new URL('my-worker.ts', import.meta.url));
 *  const worker = corsWorker.getWorker();
 */
export default class CORSWorker {
  private readonly worker: Worker;

  constructor(url: string | URL) {
    const resolved = new URL(url, self.location.href);

    // The blob + importScripts indirection exists only to load a worker from a
    // different origin (e.g. the production asset CDN), which `new Worker()`
    // forbids directly. For a same-origin worker it's unnecessary — and it
    // actively breaks the Vite dev server, whose served worker uses
    // root-relative importScripts (e.g. "/vite-assets/@vite/env") that cannot
    // resolve against a blob: base URL. Load same-origin workers directly.
    if (resolved.origin === self.location.origin) {
      this.worker = new Worker(resolved);
      return;
    }

    const objectURL = URL.createObjectURL(
      new Blob([`importScripts(${JSON.stringify(resolved.toString())});`], {
        type: "text/javascript",
      }),
    );
    this.worker = new Worker(objectURL);
    URL.revokeObjectURL(objectURL);
  }

  getWorker() {
    return this.worker;
  }
}
